import { useState, type ReactNode } from "react";
import {
  DndContext, DragOverlay, KeyboardSensor, MouseSensor, TouchSensor, useDraggable, useDroppable, useSensor, useSensors,
  type DragEndEvent, type DragStartEvent, type KeyboardCoordinateGetter, type UniqueIdentifier,
} from "@dnd-kit/core";
import { STAGES, sumValues, usd, type Stage } from "../stages";
import { ErrorNote, plural, useAction } from "../ui";

/**
 * Keyboard moves go a whole column per arrow press. dnd-kit's default steps
 * 25px, which never leaves the column a card starts in. The card is placed
 * just under the target column's header, so it lands inside it however long
 * the source column is. Up/Down do nothing: order within a column is not a
 * thing this board stores.
 */
function byColumn(stageOf: (id: UniqueIdentifier) => Stage | undefined): KeyboardCoordinateGetter {
  return (event, { active, context, currentCoordinates }) => {
    const step = event.code === "ArrowRight" ? 1 : event.code === "ArrowLeft" ? -1 : 0;
    if (!step) return undefined;
    event.preventDefault();
    const { over, droppableRects, collisionRect } = context;
    // Right after pick-up dnd-kit may not have computed `over` yet; the card's
    // own column is where it starts.
    const current = (over?.id as Stage | undefined) ?? stageOf(active);
    const from = Math.max(0, STAGES.findIndex((s) => s.value === current));
    const to = STAGES[Math.min(STAGES.length - 1, Math.max(0, from + step))];
    const rect = droppableRects.get(to.value);
    if (!rect || !collisionRect) return currentCoordinates;
    return { x: rect.left + (rect.width - collisionRect.width) / 2, y: rect.top + 48 };
  };
}

export type BoardItem = { id: number; stage: Stage; deal_value: number | null };

/**
 * One column per stage. Drag a card to another column to change its stage:
 * the card moves at once, the write follows, and a failed write puts the card
 * back and says why. Click (or Enter on) a card to open it.
 *
 * Sensors: mouse after 6px of movement (so a click stays a click); touch after
 * a 250ms press (so a swipe still scrolls the board on a phone); keyboard
 * (Space to lift, Left/Right to change column, Space to drop).
 */
export function Board<T extends BoardItem>({ items, label, renderCard, onMove, onOpen }: {
  items: T[];
  label: (item: T) => string;
  renderCard: (item: T) => ReactNode;
  onMove: (item: T, stage: Stage) => Promise<void>;
  onOpen: (item: T) => void;
}) {
  const action = useAction();
  const [moved, setMoved] = useState<Map<number, Stage>>(new Map());
  const [dragging, setDragging] = useState<T | null>(null);
  // Optimistic overrides win until the parent's items catch up with them.
  const shown = items.map((it) => (moved.has(it.id) ? { ...it, stage: moved.get(it.id)! } : it));
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: byColumn((id) => shown.find((it) => it.id === id)?.stage) }),
  );

  const onDragStart = (e: DragStartEvent) => setDragging(shown.find((it) => it.id === e.active.id) ?? null);
  const onDragEnd = async (e: DragEndEvent) => {
    setDragging(null);
    const item = shown.find((it) => it.id === e.active.id);
    const to = e.over?.id as Stage | undefined;
    if (!item || !to || to === item.stage) return;
    setMoved((m) => new Map(m).set(item.id, to));
    await action.run(() => onMove(item, to)); // on failure the card falls back to its saved stage and the error shows
    setMoved((m) => { const r = new Map(m); r.delete(item.id); return r; });
  };

  return (
    <>
      <ErrorNote error={action.error} />
      <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={() => setDragging(null)}
        accessibility={{ announcements: {
          onDragStart: ({ active }) => `Picked up ${label(shown.find((i) => i.id === active.id)!)}.`,
          onDragOver: ({ over }) => (over ? `Over ${STAGES.find((s) => s.value === over.id)?.label}.` : "Not over a column."),
          onDragEnd: ({ over }) => (over ? `Dropped in ${STAGES.find((s) => s.value === over.id)?.label}.` : "Dropped."),
          onDragCancel: () => "Move cancelled.",
        } }}>
        <div className="board">
          {STAGES.map((s, i) => {
            const cards = shown.filter((it) => it.stage === s.value);
            return (
              <Column key={s.value} stage={s.value} title={s.label} index={i} count={cards.length} value={sumValues(cards)}>
                {cards.map((it) => <Card key={it.id} item={it} label={label(it)} onOpen={() => onOpen(it)}>{renderCard(it)}</Card>)}
              </Column>
            );
          })}
        </div>
        <DragOverlay>{dragging ? <div className="kcard overlay">{renderCard(dragging)}</div> : null}</DragOverlay>
      </DndContext>
    </>
  );
}

function Column({ stage, title, index, count, value, children }: { stage: Stage; title: string; index: number; count: number; value: number; children: ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: stage });
  return (
    <section ref={setNodeRef} className={isOver ? "column over" : "column"} aria-label={title} data-stage={stage}>
      <header className="column-head">
        <span className="column-title"><span className={`stage-dot s${index}`} aria-hidden="true" />{title}</span>
        <span className="muted small">{plural(count, "deal")} · {usd(value)}</span>
      </header>
      <div className="column-cards">
        {count === 0 ? <p className="column-empty muted small">Drop here</p> : children}
      </div>
    </section>
  );
}

function Card({ item, label, onOpen, children }: { item: BoardItem; label: string; onOpen: () => void; children: ReactNode }) {
  const { setNodeRef, attributes, listeners, isDragging } = useDraggable({ id: item.id });
  return (
    <div ref={setNodeRef} {...attributes} {...listeners} className={isDragging ? "kcard ghost" : "kcard"}
      aria-label={`${label}. Press Enter to open, Space to move.`}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.preventDefault(); onOpen(); return; }
        listeners?.onKeyDown?.(e);
      }}>
      {children}
    </div>
  );
}
